import { createContext, useState } from 'react';

import { ICredential, ICredentialForm } from 'core/Api';
import { TokenExCardTranslations } from 'core/Hooks';
import { atom, createStore } from 'jotai';
import { DevTools } from 'jotai-devtools';

const StoreContext = createContext<ReturnType<typeof createStore> | undefined>(undefined);

/**
 * This is the cause Id Atom to be referenced within store
 */
const storeCauseIdAtom = atom<string | undefined>(undefined);
storeCauseIdAtom.debugLabel = 'Store_CauseId';

/**
 * Current Cart Id of the store
 */
const cartIdAtom = atom<undefined | string>(undefined);
cartIdAtom.debugLabel = 'Store_CartId';

/**
 *  A validated, cart credential returned from stripe
 */
const cartCredentialAtom = atom<ICredential | undefined>(undefined);

/**
 *  Prior to a credit card being validated, this is the pending credential form
 */
const credentialFormAtom = atom<ICredentialForm | undefined>(undefined);
credentialFormAtom.debugLabel = 'store_cartCredentialForm';

/**
 * This is used for when the user is choosing to use an existing credential during donation checkout
 */
const existingCredentialAtom = atom<ICredential | undefined>(undefined);
existingCredentialAtom.debugLabel = 'Store_ExistingCredent';

/**
 * A representation of the card type of the current cart credential
 */
const cartCardTypeAtom = atom<TokenExCardTranslations | undefined>((get) => {
  const credentialForm = get(credentialFormAtom);
  const existingCredential = get(existingCredentialAtom);
  if (credentialForm) return TokenExCardTranslations[credentialForm.metadata.card_type];
  if (existingCredential) return TokenExCardTranslations[existingCredential.card_type];
  return undefined;
});

const StoreProvider = ({ children }: { children: React.ReactNode }) => {
  const [store] = useState(() => createStore());

  return (
    <StoreContext.Provider value={store}>
      {children}
      <DevTools />
    </StoreContext.Provider>
  );
};

const useStoreProvider = () => {
  return {
    cartCredentialAtom,
    cartIdAtom,
    storeCauseIdAtom,
    credentialFormAtom,
    cartCardTypeAtom,
    existingCredentialAtom,
  };
};

// Should I create a hook that can return the atoms to prevent the need for them to be used independently?
export { StoreProvider, cartIdAtom, storeCauseIdAtom, useStoreProvider };
